// src/features/records/components/shared/AttachmentPreviewStrip.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styles from './AttachmentPreviewStrip.module.css';
import AttachmentThumb from './AttachmentThumb';

/**
 * AttachmentPreviewStrip - Displays attachment previews from attachmentsSummary
 * Used when full attachment data is not loaded yet
 */
const AttachmentPreviewStrip = ({ previews = [], count = 0 }) => {
  const { t } = useTranslation();
  if (!previews || previews.length === 0) {
    return null;
  }

  const displayPreviews = previews.slice(0, 3);
  const remainingCount = count - displayPreviews.length;

  // Transform preview data to attachment format for AttachmentThumb
  const transformPreviewToAttachment = (preview) => {
    const isImage = preview.fileType?.startsWith('image/');
    const isVideo = preview.fileType?.startsWith('video/');
    
    return {
      id: preview.id,
      type: isVideo ? 'video' : (isImage ? 'image' : 'file'),
      // Use thumbnail as the storage path - AttachmentThumb will handle URL resolution
      storagePath: preview.thumbnail,
      // For thumbnails, we can assume the thumbnail field is already the appropriate size
      thumbnails: preview.thumbnail ? {
        '400': preview.thumbnail
      } : {},
      metadata: {
        filename: preview.filename || t('records.detail.attachments.fallback_name', 'Attachment')
      }
    };
  };

  return (
    <div className={styles.previewStrip}>
      {displayPreviews.map((preview, index) => {
        const attachment = transformPreviewToAttachment(preview);
        
        return (
          <div 
            key={preview.id || index} 
            className={styles.previewItem}
          >
            {attachment.type === 'image' || attachment.type === 'video' ? (
              <AttachmentThumb 
                attachment={attachment} 
                variant="compact"
                className={styles.previewThumb}
              />
            ) : (
              <div className={styles.previewPlaceholder}>
                <span className={styles.fileType}>
                  {getFileTypeDisplay(preview.fileType, t)}
                </span>
              </div>
            )}
          </div>
        );
      })}
      
      {remainingCount > 0 && (
        <div className={styles.moreCount}>
          {t('records.detail.attachments.more_count', {
            count: remainingCount,
            defaultValue: '+{{count}}'
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Get display name for file type
 */
function getFileTypeDisplay(fileType, t) {
  if (!fileType) {
    return t('records.detail.attachments.file_type_default', 'file');
  }
  
  const typeMap = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'text/plain': 'TXT',
    'application/zip': 'ZIP',
  };
  
  return typeMap[fileType]
    || fileType.split('/')[1]?.toUpperCase()
    || t('records.detail.attachments.file_type_default', 'file');
}

AttachmentPreviewStrip.propTypes = {
  previews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    fileType: PropTypes.string,
    thumbnail: PropTypes.string,
    uploadedAt: PropTypes.object
  })),
  count: PropTypes.number
};

export default React.memo(AttachmentPreviewStrip);
